import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { EnvelopeIcon } from '@phosphor-icons/react';
import { getInitialBadgeColor } from '@/lib/utils/color.utils';
import { getInitials } from '@/lib/utils/name.utils';
import { GuestLogContent } from '@/content/GuestLogContent';
import type { Guest } from '@/lib/types/database';

export interface GuestDetailsDrawerProps {
  /** Controls whether the drawer is open */
  open: boolean;
  /** Callback when the drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** The guest to display */
  guest: Guest | null;
}

/**
 * Format visit date and time for display
 */
function formatVisitDateTime(visitDate: string, createdAt: string): string {
  const visit = new Date(visitDate);
  const created = new Date(createdAt);

  // Format visit date
  const month = visit.toLocaleDateString('en-US', { month: 'short' });
  const day = visit.getDate();
  const year = visit.getFullYear();

  // Format created time (when guest was registered)
  const time = created.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${month} ${day}, ${year} | ${time}`;
}

/**
 * Drawer component that displays guest information
 * Shows guest details, registered by info, reason for visit, and time of visit
 */
export function GuestDetailsDrawer({
  open,
  onOpenChange,
  guest,
}: GuestDetailsDrawerProps) {
  if (!guest) {
    return null;
  }

  const guestName = `${guest.first_name} ${guest.last_name}`.trim();
  const guestInitials = getInitials(guestName);
  const guestBadgeColor = getInitialBadgeColor(guestName);

  const registeredByName = guest.registered_by_name || 'Unknown';
  const registeredByInitials = getInitials(registeredByName);
  const registeredByBadgeColor = getInitialBadgeColor(registeredByName);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle className="text-[24px] mb-6">
              {GuestLogContent.drawer.title}
            </DrawerTitle>

            {/* Added by section */}
            <div className="mb-6">
              <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                {GuestLogContent.drawer.addedBy}
              </DrawerDescription>
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-md text-small text-slate-50 ${registeredByBadgeColor}`}
                >
                  {registeredByInitials}
                </span>
                <span className="text-body text-slate-50">
                  {registeredByName}
                </span>
              </div>
            </div>

            {/* Guest Info section */}
            <div className="mb-6">
              <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                {GuestLogContent.drawer.guestInfo}
              </DrawerDescription>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-md text-small text-slate-50 ${guestBadgeColor}`}
                >
                  {guestInitials}
                </span>
                <span className="text-body text-slate-50">{guestName}</span>
              </div>
              <div className="flex items-center gap-2 text-body text-slate-50">
                <EnvelopeIcon size={20} weight="regular" />
                <span>{guest.email}</span>
              </div>
            </div>

            {/* Reason for Visit section */}
            <div className="mb-6">
              <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                {GuestLogContent.drawer.reasonForVisit}
              </DrawerDescription>
              <p className="text-body text-slate-50">
                {guest.reason_for_visit}
              </p>
            </div>

            {/* Time of Visit section */}
            <div>
              <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                {GuestLogContent.drawer.timeOfVisit}
              </DrawerDescription>
              <p className="text-body text-slate-50">
                {formatVisitDateTime(guest.visit_date, guest.created_at)}
              </p>
            </div>
          </DrawerHeader>

          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="h-16">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

